(() => {
  const shopRoot = window.Shopify?.routes?.root || '/';
  const formatMoney = (cents, currency) => window.TaztyShipping?.formatMoney?.(cents, currency)
    || new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(cents || 0) / 100);
  const getThresholdRate = (...args) => window.TaztyShipping?.getThresholdRate?.(...args) ?? null;
  const updateCartCount = async () => {
    const response = await fetch(`${shopRoot}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000), headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('The updated cart could not be confirmed.');
    let cart = await response.json();
    let warning = '';
    try { cart = await window.TaztyPackDiscounts?.sync(cart) || cart; }
    catch (_) { warning = 'Product added to cart, but the pack discount could not be confirmed. Open your cart to check it before adding again.'; }
    document.querySelectorAll('.site-header__cart b').forEach(node => { node.textContent = cart.item_count; });
    document.dispatchEvent(new CustomEvent('tazty:cart-updated', { detail: { ...cart, openDrawer: false } }));
    return warning;
  };
  const addItems = async (items, notify = true) => {
    const response = await fetch(`${shopRoot}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || 'These items could not be added.');
    const cartWarning = await updateCartCount().catch(() => 'Product added to cart. Open your cart to confirm the updated total before adding again.');
    if (notify) {
      const message = items.length > 1 ? 'Products added to cart' : `${result.items?.[0]?.product_title || 'Product'} added to cart`;
      document.dispatchEvent(new CustomEvent('tazty:cart-notice', { detail: { message: cartWarning || message } }));
    }
    return { ...result, cartWarning };
  };
  const init = root => {
    if (!root || root.dataset.tzInitialized === 'true') return;
    root.dataset.tzInitialized = 'true';
    const getActiveCurrency = () => window.Shopify?.currency?.active
      || root.querySelector('[data-shipping-bar]')?.dataset.marketCurrency
      || root.dataset.currency
      || 'USD';
    const unitPrice = Number(root.dataset.unitPrice || 0);
    const form = root.querySelector('.tz-product__form');
    const baseQuantity = root.querySelector('[data-base-quantity]');
    const finalQuantity = root.querySelector('[data-product-final-quantity]');
    const addButton = root.querySelector('[data-product-add]');
    const addTotal = root.querySelector('[data-product-add-total]');
    const status = root.querySelector('[data-product-status]');
    const initialPack = root.querySelector('[data-pack-option].is-active');
    const packOptions = [...root.querySelectorAll('[data-pack-option]')];
    const hasPackOptions = packOptions.length > 0;
    let packQuantity = Number(initialPack?.dataset.packQuantity || 1);
    let packDiscount = Number(initialPack?.dataset.packDiscount || 0);
    let packLabel = initialPack?.dataset.packLabel || '';
    const rawQuantityLimit = baseQuantity?.max;
    const parsedQuantityLimit = Number(rawQuantityLimit);
    const hasQuantityLimit = rawQuantityLimit !== '' && rawQuantityLimit != null && Number.isFinite(parsedQuantityLimit);
    const quantityLimit = hasQuantityLimit ? Math.max(0, Math.floor(parsedQuantityLimit)) : Infinity;
    // The visible field is the number of individual products, not a pack multiplier.
    const selectedQuantity = () => {
      const value = Number(baseQuantity?.value ?? packQuantity);
      const normalized = Math.max(1, Number.isFinite(value) ? Math.floor(value) : 1);
      return hasQuantityLimit ? Math.min(quantityLimit, normalized) : normalized;
    };
    const discountForQuantity = quantity => {
      if (packDiscount <= 0 || quantity < 2) return 0;
      const tierQuantity = quantity >= 4 ? 4 : 2;
      return Number(packOptions.find(option => Number(option.dataset.packQuantity) === tierQuantity)?.dataset.packDiscount || 0);
    };
    const calculatedCents = () => Math.round(unitPrice * selectedQuantity() * (100 - discountForQuantity(selectedQuantity())) / 100);
    const packProperties = quantity => {
      if (!hasPackOptions) return {};
      const properties = { _Pack: packLabel || 'Single pack' };
      const discount = discountForQuantity(quantity);
      if (discount > 0) {
        properties['_Displayed pack discount'] = `${discount}%`;
        properties['_Tazty discounted quantity'] = String(quantity);
      }
      return properties;
    };
    const sync = () => {
      const quantity = selectedQuantity();
      const currency = getActiveCurrency();
      if (baseQuantity) baseQuantity.value = quantity;
      if (finalQuantity) finalQuantity.value = quantity;
      if (addTotal) addTotal.textContent = formatMoney(calculatedCents(), currency);
      window.TaztyShipping?.update?.(root.querySelector('[data-shipping-bar]'), calculatedCents(), currency);

    };
    root.querySelectorAll('[data-gallery-thumb]').forEach(button => button.addEventListener('click', () => {
      const image = root.querySelector('.tz-product__main-image');
      const nextSource = button.dataset.imageSrc;
      if (!image || !nextSource) return;
      root.querySelectorAll('[data-gallery-thumb]').forEach(item => { item.classList.remove('is-active'); item.setAttribute('aria-pressed', 'false'); });
      button.classList.add('is-active'); button.setAttribute('aria-pressed', 'true'); image.classList.add('is-changing');
      window.clearTimeout(root.gallerySwapTimer);
      root.gallerySwapTimer = window.setTimeout(() => {
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = nextSource;
        image.alt = button.dataset.imageAlt || '';
        image.classList.remove('is-changing');
      }, 120);
    }));
    root.querySelectorAll('[data-pack-option]').forEach(button => {
      const requestedPackQuantity = Number(button.dataset.packQuantity || 1);
      const exceedsAvailableStock = hasQuantityLimit && requestedPackQuantity > quantityLimit;
      button.disabled = exceedsAvailableStock;
      button.setAttribute('aria-disabled', String(exceedsAvailableStock));
      button.addEventListener('click', () => {
      if (button.disabled) return;
      root.querySelectorAll('[data-pack-option]').forEach(item => { item.classList.remove('is-active'); item.setAttribute('aria-pressed', 'false'); });
      button.classList.add('is-active'); button.setAttribute('aria-pressed', 'true');
      packQuantity = Number(button.dataset.packQuantity || 1); packDiscount = Number(button.dataset.packDiscount || 0); packLabel = button.dataset.packLabel || '';
      if (baseQuantity) baseQuantity.value = packQuantity;
      const selected = root.querySelector('[data-selected-pack]'); if (selected) selected.textContent = `${packLabel} selected`;
      sync();
      });
    });
    root.querySelector('[data-bundle-variant-select]')?.addEventListener('change', event => {
      const option = event.currentTarget.selectedOptions[0];
      if (!option) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', option.value);
      window.location.assign(url.toString());
    });
    root.querySelectorAll('[data-bundle-toggle]').forEach(button => {
      const grid = button.closest('.tz-bundle')?.querySelector('[data-bundle-grid]');
      if (!grid) return;
      grid.classList.add('is-collapsed');
      button.hidden = false;
      button.setAttribute('aria-expanded', 'false');
      button.addEventListener('click', () => {
        const expanded = grid.classList.toggle('is-collapsed') === false;
        button.setAttribute('aria-expanded', String(expanded));
        button.firstChild.textContent = `${expanded ? button.dataset.lessLabel : button.dataset.moreLabel} `;
        button.querySelector('span').textContent = expanded ? '↑' : '↓';
      });
    });
    root.querySelector('[data-quantity-minus]')?.addEventListener('click', () => { if (!baseQuantity) return; baseQuantity.value = Math.max(1, Number(baseQuantity.value || 1) - 1); sync(); });
    root.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
      if (!baseQuantity) return;
      const nextQuantity = Math.max(1, Number(baseQuantity.value || 1) + 1);
      if (hasQuantityLimit && nextQuantity > quantityLimit) {
        if (status) status.textContent = `The maximum available quantity is ${quantityLimit}.`;
        return;
      }
      baseQuantity.value = nextQuantity;
      sync();
    });
    baseQuantity?.addEventListener('change', () => {
      const requestedQuantity = Number(baseQuantity.value);
      if (hasQuantityLimit && Number.isFinite(requestedQuantity) && requestedQuantity > quantityLimit && status) {
        status.textContent = `The maximum available quantity is ${quantityLimit}.`;
      }
      sync();
    });
    form?.addEventListener('submit', async event => {
      event.preventDefault(); if (!addButton || addButton.disabled) return;
      sync();
      addButton.disabled = true; addButton.setAttribute('aria-busy', 'true'); if (status) status.textContent = '';
      try {
        const quantity = Number(finalQuantity.value);
        const result = await addItems([{ id: Number(form.querySelector('[data-product-variant-id]').value), quantity, properties: packProperties(quantity) }]);
        if (status) status.textContent = result.cartWarning || 'Added to cart.';
      } catch (error) { if (status) status.textContent = error.message; }
      finally { addButton.disabled = false; addButton.removeAttribute('aria-busy'); }
    });
    root.querySelector('[data-product-buy-now]')?.addEventListener('click', async event => {
      const button = event.currentTarget; button.disabled = true;
      sync();
      try {
        const quantity = Number(finalQuantity.value);
        const result = await addItems([{ id: Number(form.querySelector('[data-product-variant-id]').value), quantity, properties: packProperties(quantity) }], false);
        if (result.cartWarning) {
          if (status) status.textContent = result.cartWarning;
          button.disabled = false;
          return;
        }
        window.location.href = `${shopRoot}checkout`;
      }
      catch (error) { if (status) status.textContent = error.message; button.disabled = false; }
    });
    root.querySelectorAll('[data-fbt-add]').forEach(button => button.addEventListener('click', async () => {
      const item = button.closest('[data-fbt-item]'); const fbtStatus = root.querySelector('[data-fbt-status]'); button.disabled = true;
      try { await addItems([{ id: Number(item.dataset.variantId), quantity: 1 }]); if (fbtStatus) fbtStatus.textContent = 'Product added to cart.'; }
      catch (error) { if (fbtStatus) fbtStatus.textContent = error.message; button.disabled = false; }
    }));
    root.querySelector('[data-fbt-add-all]')?.addEventListener('click', async event => {
      const button = event.currentTarget; const items = [...root.querySelectorAll('[data-fbt-item][data-available=true]')].map(item => ({ id: Number(item.dataset.variantId), quantity: 1 })); const fbtStatus = root.querySelector('[data-fbt-status]');
      if (!items.length) return; button.disabled = true;
      try { await addItems(items); if (fbtStatus) fbtStatus.textContent = 'All available products added to cart.'; }
      catch (error) { if (fbtStatus) fbtStatus.textContent = error.message; }
      finally { button.disabled = false; }
    });
    root.querySelector('[data-product-share]')?.addEventListener('click', async () => { try { if (navigator.share) await navigator.share({ title: document.title, url: location.href }); else { await navigator.clipboard.writeText(location.href); if (status) status.textContent = 'Product link copied.'; } } catch (_) {} });
    ['currency:change', 'shopify:currency:change', 'localization:change'].forEach(eventName => document.addEventListener(eventName, sync));
    sync();
  };
  const initAll = scope => {
    if (scope?.matches?.('[data-tz-product]')) init(scope);
    scope?.querySelectorAll?.('[data-tz-product]').forEach(init);
  };
  const boot = () => initAll(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  window.addEventListener('pageshow', boot);
  document.addEventListener('shopify:section:load', event => initAll(event.target));
})();
